import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Lock } from 'lucide-react';
import { profileService } from '../../services/profile/profileService';
import { useAgent } from '../agent/AgentContext';
import { useSubscription } from '../subscription/SubscriptionContext';
import { formatDate, formatCurrency, getPaymentMethodLabel, generateInvoiceDoc } from '../../pages/Profile/utils/profileHelpers';
import CustomInput from '../../components/forms/CustomInput';

const ProfileContext = createContext();

export const useProfile = () => useContext(ProfileContext);

export const ProfileProvider = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const { setAgent, setUserData } = useAgent();

  // Tab & Route Synchronization
  const validTabs = ['account', 'business', 'subscription'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(tabFromUrl) ? tabFromUrl : 'account'
  );

  useEffect(() => {
    const currentParam = searchParams.get('tab');
    if (currentParam && validTabs.includes(currentParam) && currentParam !== activeTab) {
      setActiveTab(currentParam);
    }
  }, [searchParams]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
  };

  // Subscription Context
  const {
    subscription,
    currentPlan,
    latestRequest,
    trialDaysRemaining,
    isTrialExpired,
    subscriptionDaysRemaining,
    loading: subscriptionLoading,
    paymentHistory = [],
    vouchersCreated,
    vouchersLimit,
    cycleStartDate,
    supplierInfo,
    refreshSubscription,
    cancelSubscription: contextCancelSubscription,
    resumeSubscription: contextResumeSubscription
  } = useSubscription();

  const effectiveSub = subscription || {
    status: 'trial',
    plan_type: 'trial',
    custom_voucher_limit: 10,
    trial_start: null,
    trial_end: null
  };

  const isTrialUser = Boolean(
    !subscription ||
    effectiveSub.status === 'trial' ||
    (effectiveSub.plan_type === 'trial' && effectiveSub.status !== 'active')
  );

  // Dismissed state for rejected request alerts (persisted in localStorage)
  const [dismissedRejectedId, setDismissedRejectedId] = useState(() => {
    try {
      return localStorage.getItem('trrip_dismissed_rejected_req') || null;
    } catch {
      return null;
    }
  });

  const handleDismissRejectedBanner = (id) => {
    setDismissedRejectedId(String(id));
    try {
      localStorage.setItem('trrip_dismissed_rejected_req', String(id));
    } catch (e) {
      console.warn(e);
    }
  };

  // OTP & Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [otpMethod, setOtpMethod] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Subscription Edit States
  const [isEditingGst, setIsEditingGst] = useState(false);
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [gstinValue, setGstinValue] = useState('');
  const [gstNameValue, setGstNameValue] = useState('');
  const [gstAddressValue, setGstAddressValue] = useState('');
  const [invoiceEmailValue, setInvoiceEmailValue] = useState('');

  // Modals States
  const [showCycModal, setShowCycModal] = useState(false);
  const [showPmModal, setShowPmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isSwitchingAnnual, setIsSwitchingAnnual] = useState(false);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

  // Subscription Request & Downgrade / Cycle Change States
  const [allPlans, setAllPlans] = useState([]);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedDowngradePlan, setSelectedDowngradePlan] = useState(null);
  const [showSwitchMonthlyModal, setShowSwitchMonthlyModal] = useState(false);
  const [requestReason, setRequestReason] = useState('Pricing is too high / not cost effective');
  const [requestComments, setRequestComments] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestSuccessMessage, setRequestSuccessMessage] = useState(null);
  const [isAuthorizingMandate, setIsAuthorizingMandate] = useState(false);

  // Mid-Cycle Upgrade & Annual Support Modals
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showAnnualSupportModal, setShowAnnualSupportModal] = useState(false);

  const [showCheckoutPage, setShowCheckoutPage] = useState(false);

  useEffect(() => {
    profileService.getAvailablePlans()
      .then(res => setAllPlans(res.plans || []))
      .catch(() => { });
  }, []);

  const handleUpgradeClick = (plan) => {
    if (subscription?.billing_interval === 'year') {
      setShowAnnualSupportModal(true);
      return;
    }
    const realPlan = allPlans.find(p => (p.slug || '').toLowerCase() === (plan?.slug || '').toLowerCase() || p._id === plan?._id) || plan;
    const planParam = realPlan?._id && !String(realPlan._id).includes('default')
      ? realPlan._id
      : (realPlan?.slug || 'pro');
    navigate(`/checkout?plan=${planParam}&interval=month&from=profile`);
  };

  const handleConfirmUpgrade = () => {
    setShowUpgradeModal(false);
    setShowCheckoutPage(true);
  };

  const triggerRazorpayMandate = async (prefilledMethod) => {
    if (!selectedUpgradePlan) return;
    setIsUpgrading(true);
    try {
      const checkoutData = await profileService.createCheckout(selectedUpgradePlan._id, 'month');

      const cycleEnd = subscription?.end_date || subscription?.next_billing_at || subscription?.nextBillingAt;
      const formattedCycleEnd = cycleEnd ? formatDate(cycleEnd) : 'your next cycle date';

      const rzp = new window.Razorpay({
        key: checkoutData.razorpay_key,
        subscription_id: checkoutData.subscription_id,
        name: 'Trrip',
        description: `Mandate verification (₹5 refundable today). First plan charge on ${formattedCycleEnd}`,
        prefill: {
          name: checkoutData.customer_name || '',
          email: checkoutData.customer_email || '',
          contact: checkoutData.customer_contact || '',
          method: prefilledMethod || undefined
        },
        handler: async (response) => {
          try {
            await profileService.verifyPayment({
              razorpay_subscription_id: response.razorpay_subscription_id || checkoutData.subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedUpgradePlan._id,
              billing_interval: 'month'
            });
            toast.success(`Mandate Authorized! Upgraded to ${selectedUpgradePlan.name} Tier.`);
            setShowCheckoutPage(false);
            refreshSubscription();
          } catch (verifyError) {
            toast.error('Payment mandate verification failed. Please try again.');
          } finally {
            setIsUpgrading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setIsUpgrading(false);
          }
        }
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to initialize payment mandate');
      setIsUpgrading(false);
    }
  };

  const [formData, setFormData] = useState({
    owner_name: '',
    business_name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    pincode: '',
    logo: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    currentPassword: '',
    newPassword: '',
    hasPassword: true,
    profiling_answers: [],
    gstin: '',
    gst_legal_name: '',
    gst_address: '',
    invoice_email: ''
  });

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await profileService.getProfile();
        const data = response.data || response;
        const initialGstin = data.gstin || '';
        const initialGstName = data.gst_legal_name || data.company_name || '';
        const initialGstAddress = data.gst_address || data.address || '';
        const initialInvoiceEmail = data.invoice_email || '';

        setFormData({
          owner_name: data.name || '',
          business_name: data.company_name || '',
          email: data.email || '',
          phone_number: data.phone_number || '',
          address: data.address || '',
          city: data.city || '',
          pincode: data.pincode || '',
          logo: data.logo || '',
          account_holder_name: data.account_holder_name || '',
          account_number: data.account_number || '',
          ifsc_code: data.ifsc_code || '',
          branch: data.branch_name || data.branch || '',
          currentPassword: '',
          newPassword: '',
          hasPassword: data.hasPassword ?? true,
          profiling_answers: data.profiling_answers || [],
          gstin: initialGstin,
          gst_legal_name: initialGstName,
          gst_address: initialGstAddress,
          invoice_email: initialInvoiceEmail
        });
        setGstinValue(initialGstin);
        setGstNameValue(initialGstName);
        setGstAddressValue(initialGstAddress);
        setInvoiceEmailValue(initialInvoiceEmail);
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error('Session expired. Please login again.');
          window.location.href = '/login';
        }
      }
    };
    fetchUserData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendOtp = async (method) => {
    setIsLoading(true);
    setOtpMethod(method);
    try {
      const payload = method === 'email'
        ? { email: formData.email, role: 'Agent' }
        : { phone_number: formData.phone_number, role: 'Agent' };

      await profileService.sendOtp(method, payload);
      toast.success(`OTP sent to your ${method}`);
      setOtpSent(true);
      setOtp(['', '', '', '', '', '']);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value !== '' && index < 5) {
        document.getElementById(`otp-input-${index + 1}`)?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedData) return;
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pastedData[i] || '';
    }
    setOtp(newOtp);
    const nextEmptyIndex = newOtp.findIndex(val => val === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    document.getElementById(`otp-input-${focusIndex}`)?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) { toast.error('Enter 6-digit OTP'); return; }

    setIsLoading(true);
    try {
      const payload = otpMethod === 'email'
        ? { email: formData.email, otp: otpString }
        : { phone_number: formData.phone_number, otp: otpString };

      await profileService.verifyOtp(otpMethod, payload);
      toast.success('OTP verified successfully!');
      setOtpVerified(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordFinal = async () => {
    if (!formData.newPassword) { toast.error('Please enter a new password'); return; }

    setIsLoading(true);
    try {
      const payload = {
        newPassword: formData.newPassword,
        ...(otpMethod === 'email' ? { email: formData.email } : { phone_number: formData.phone_number })
      };
      await profileService.resetPassword(payload);
      toast.success('Password updated successfully!');
      setShowForgotPassword(false);
      setOtpSent(false);
      setOtpVerified(false);
      setIsEditing(false);
      setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '' }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const uploadData = new FormData();
    uploadData.append('file', file);
    const toastId = toast.loading('Uploading logo...');
    try {
      const response = await profileService.uploadLogo(uploadData);
      if (response?.fileUrl) {
        const newLogoUrl = response.fileUrl;
        setFormData((prev) => ({ ...prev, logo: newLogoUrl }));

        const user_id = localStorage.getItem('user_id');
        if (user_id) {
          try {
            await profileService.updateProfile(user_id, { logo: newLogoUrl });
            if (setAgent) setAgent(prev => ({ ...(prev || {}), logo: newLogoUrl }));
            if (setUserData) setUserData(prev => ({ ...(prev || {}), logo: newLogoUrl, agent: { ...(prev?.agent || {}), logo: newLogoUrl } }));
          } catch (saveErr) {
            console.warn('Auto-save profile logo error:', saveErr);
          }
        }

        toast.dismiss(toastId);
        toast.success('Logo updated successfully');
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('Failed to upload logo');
    }
    e.target.value = '';
  };

  const handleRemoveLogo = async () => {
    setFormData(prev => ({ ...prev, logo: '' }));
    const user_id = localStorage.getItem('user_id');
    if (user_id) {
      try {
        await profileService.updateProfile(user_id, { logo: '' });
        if (setAgent) setAgent(prev => ({ ...(prev || {}), logo: '' }));
        if (setUserData) setUserData(prev => ({ ...(prev || {}), logo: '', agent: { ...(prev?.agent || {}), logo: '' } }));
        toast.success('Logo removed');
      } catch (err) {
        console.warn('Remove logo error:', err);
      }
    }
  };

  const handleSave = async () => {
    const user_id = localStorage.getItem('user_id');
    try {
      const payload = {
        ...formData,
        name: formData.owner_name,
        company_name: formData.business_name,
        branch_name: formData.branch,
        gstin: formData.gstin ? formData.gstin.trim().toUpperCase() : '',
        gst_legal_name: formData.gst_legal_name,
        gst_address: formData.gst_address
      };
      const response = await profileService.updateProfile(user_id, payload);
      toast.success('Profile updated successfully');
      setIsEditing(false);
      const data = response.user || response;
      if (setUserData) setUserData(data);
      if (setAgent) setAgent(data);
      setFormData(prev => ({
        ...prev,
        owner_name: data.name || prev.owner_name,
        business_name: data.company_name || prev.business_name,
        email: data.email || prev.email,
        phone_number: data.phone_number || prev.phone_number,
        address: data.address || prev.address,
        city: data.city || prev.city,
        pincode: data.pincode || prev.pincode,
        logo: data.logo || prev.logo,
        account_holder_name: data.account_holder_name || prev.account_holder_name,
        account_number: data.account_number || prev.account_number,
        ifsc_code: data.ifsc_code || prev.ifsc_code,
        branch: data.branch_name || prev.branch,
        currentPassword: '',
        newPassword: '',
        hasPassword: data.hasPassword ?? prev.hasPassword
      }));
      setGstinValue(formData.gstin ? formData.gstin.trim().toUpperCase() : '');
      setGstNameValue(formData.gst_legal_name || formData.business_name || '');
      setGstAddressValue(formData.gst_address || formData.address || '');
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleUpdatePasswordDirect = async () => {
    if (formData.hasPassword && !formData.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!formData.newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (formData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }
    setIsLoading(true);
    try {
      const user_id = localStorage.getItem('user_id');
      await profileService.updateProfile(user_id, {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      toast.success('Password updated successfully!');
      setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '' }));
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  // Subscription Actions
  const handleSaveGst = async () => {
    if (!gstinValue.trim()) {
      toast.error('GSTIN / GST Number is required');
      return;
    }
    setIsLoading(true);
    try {
      const user_id = localStorage.getItem('user_id');
      const payload = {
        gstin: gstinValue.trim().toUpperCase(),
        gst_legal_name: gstNameValue.trim(),
        gst_address: gstAddressValue.trim()
      };
      await profileService.updateProfile(user_id, payload);
      toast.success('GST details updated successfully');
      setFormData(prev => ({
        ...prev,
        gstin: payload.gstin,
        gst_legal_name: payload.gst_legal_name,
        gst_address: payload.gst_address
      }));
      setIsEditingGst(false);
    } catch (error) {
      toast.error('Failed to update GST details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveInvoiceEmail = async () => {
    if (!invoiceEmailValue) {
      toast.error('Invoice email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(invoiceEmailValue)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const user_id = localStorage.getItem('user_id');
      await profileService.updateProfile(user_id, { invoice_email: invoiceEmailValue });
      toast.success('Invoice recipient updated — invoices will also go to the registered account email.');
      setFormData(prev => ({ ...prev, invoice_email: invoiceEmailValue }));
      setIsEditingInvoice(false);
    } catch (error) {
      toast.error('Failed to update invoice email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscriptionRequest = async () => {
    setIsSubmittingRequest(true);
    try {
      const fullReason = `${requestReason}${requestComments.trim() ? ': ' + requestComments.trim() : ''}`;
      const res = await profileService.createSubscriptionRequest({
        type: 'cancellation',
        reason: fullReason
      });
      setRequestSuccessMessage(res.message || 'Your cancellation request has been submitted. Our team will coordinate with you in 24–48 hours.');
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit cancellation request');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleDowngradeRequest = async () => {
    if (!selectedDowngradePlan) return;
    setIsSubmittingRequest(true);
    try {
      const fullReason = `${requestReason}${requestComments.trim() ? ': ' + requestComments.trim() : ''}`;
      const res = await profileService.createSubscriptionRequest({
        type: 'downgrade',
        target_plan_id: selectedDowngradePlan._id,
        target_billing_interval: subscription?.billing_interval || 'month',
        reason: fullReason
      });
      setRequestSuccessMessage(res.message || `Your request to downgrade to ${selectedDowngradePlan.name} has been submitted. Our team will coordinate with you in 24–48 hours.`);
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit downgrade request');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleSwitchMonthlyRequest = async () => {
    setIsSubmittingRequest(true);
    try {
      const currentPlanId = subscription?.plan_id?._id || subscription?.plan_id;
      const fullReason = `${requestReason}${requestComments.trim() ? ': ' + requestComments.trim() : ''}`;
      const res = await profileService.createSubscriptionRequest({
        type: 'switch_billing_cycle',
        target_plan_id: currentPlanId,
        target_billing_interval: 'month',
        reason: fullReason
      });
      setRequestSuccessMessage(res.message || 'Your request to switch to monthly billing has been submitted. Our team will coordinate with you in 24–48 hours.');
      refreshSubscription();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit switch request');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleAuthorizePendingPlan = () => {
    const pending = subscription?.pending_change;
    if (!pending) return;
    const targetPlanId = pending.target_plan_id?._id || pending.target_plan_id;
    const targetInterval = pending.target_billing_interval || 'month';
    const customPriceParam = pending.custom_price ? `&custom_price=${pending.custom_price}` : '';
    const customVoucherParam = pending.custom_voucher_limit ? `&custom_voucher_limit=${pending.custom_voucher_limit}` : '';
    const customTokenParam = pending.custom_link_token ? `&custom_link=${pending.custom_link_token}` : '';
    navigate(`/checkout?plan=${targetPlanId}&interval=${targetInterval}&from=profile${customPriceParam}${customVoucherParam}${customTokenParam}`);
  };

  const handleCancelSubscription = async () => {
    try {
      if (contextCancelSubscription) {
        await contextCancelSubscription('Cancelled by agent');
      }
      toast.success('Subscription cancelled successfully.');
      setShowCancelModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel subscription');
    }
  };

  const handleResumeSubscription = async () => {
    const activePlan = subscription?.plan_id;
    const planId = activePlan?._id || activePlan;
    if (!planId) {
      toast.error('Plan details not found for reactivation.');
      return;
    }

    setIsAuthorizingMandate(true);
    try {
      const checkoutData = await profileService.createCheckout(planId, subscription?.billing_interval || 'month');

      const rzp = new window.Razorpay({
        key: checkoutData.razorpay_key,
        subscription_id: checkoutData.subscription_id,
        name: 'Trrip',
        description: `Reactivate Mandate — ${activePlan?.name || 'Subscription'} Plan`,
        prefill: {
          name: checkoutData.customer_name || '',
          email: checkoutData.customer_email || '',
          contact: checkoutData.customer_contact || ''
        },
        handler: async (response) => {
          try {
            await profileService.verifyPayment({
              razorpay_subscription_id: response.razorpay_subscription_id || checkoutData.subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: planId,
              billing_interval: subscription?.billing_interval || 'month'
            });
            toast.success('Subscription reactivated successfully.');
            refreshSubscription();
          } catch (verifyError) {
            toast.error('Payment mandate authorization failed.');
          } finally {
            setIsAuthorizingMandate(false);
          }
        },
        modal: {
          ondismiss: () => {
            setIsAuthorizingMandate(false);
          }
        }
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to initialize mandate checkout');
      setIsAuthorizingMandate(false);
    }
  };

  const handleSwitchToAnnual = async () => {
    const plan = subscription?.plan_id;
    const planId = plan?._id || subscription?.plan_id;
    if (!planId) {
      navigate('/subscription');
      return;
    }
    const currentPlanName = plan?.name || 'Pro';
    setIsSwitchingAnnual(true);
    try {
      const checkoutData = await profileService.createCheckout(planId, 'year');

      const rzp = new window.Razorpay({
        key: checkoutData.razorpay_key,
        subscription_id: checkoutData.subscription_id,
        name: 'Trrip',
        description: checkoutData.total_amount_display
          ? `${checkoutData.plan_name} Plan — ₹${checkoutData.total_amount_display}/year`
          : `${checkoutData.plan_name} Plan — Annual Subscription`,
        prefill: {
          name: formData?.owner_name || '',
          email: formData?.invoice_email || formData?.email || '',
          contact: formData?.phone_number || ''
        },
        handler: async (response) => {
          try {
            await profileService.verifyPayment({
              razorpay_subscription_id: response.razorpay_subscription_id || checkoutData.subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: planId,
              billing_interval: 'year'
            });
            toast.success(`Switched to ${currentPlanName} Annual Plan! You saved 20%.`);
            setShowCycModal(false);
            refreshSubscription();
          } catch (verifyError) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setIsSwitchingAnnual(false);
          }
        },
        theme: { color: '#067978' },
        modal: { ondismiss: () => setIsSwitchingAnnual(false) }
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to initialize annual checkout');
      setIsSwitchingAnnual(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setIsUpdatingPaymentMethod(true);
    try {
      const sessionData = await profileService.createUpdatePaymentMethodSession();

      const rzp = new window.Razorpay({
        key: sessionData.razorpay_key,
        order_id: sessionData.order_id,
        name: 'Trrip',
        description: 'Authenticate New Payment Method',
        prefill: {
          name: sessionData.customer_name || formData?.owner_name || '',
          email: sessionData.customer_email || formData?.invoice_email || formData?.email || '',
          contact: sessionData.customer_contact || formData?.phone_number || ''
        },
        handler: async (response) => {
          try {
            await profileService.verifyUpdatePaymentMethod({
              razorpay_order_id: response.razorpay_order_id || sessionData.order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success('Payment method updated successfully!');
            setShowPmModal(false);
            refreshSubscription();
          } catch (verifyErr) {
            toast.error('Payment method verification failed. Please try again.');
          } finally {
            setIsUpdatingPaymentMethod(false);
          }
        },
        theme: { color: '#067978' },
        modal: { ondismiss: () => setIsUpdatingPaymentMethod(false) }
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to initialize payment method update');
      setIsUpdatingPaymentMethod(false);
    }
  };

  const handleDownloadInvoice = async (pm) => {
    try {
      toast.loading('Preparing high quality invoice...', { id: 'invoice-toast' });
      const { doc, invoiceNo } = await generateInvoiceDoc(pm, formData, subscription, paymentHistory);
      doc.save(`Invoice_${invoiceNo}.pdf`);
      toast.dismiss('invoice-toast');
      toast.success('Invoice downloaded successfully!');
    } catch (err) {
      console.error('Invoice download failed:', err);
      toast.dismiss('invoice-toast');
      toast.error('Failed to generate invoice PDF');
    }
  };

  const handleViewInvoice = async (pm) => {
    try {
      toast.loading('Opening invoice...', { id: 'preview-toast' });
      const { doc } = await generateInvoiceDoc(pm, formData, subscription, paymentHistory);
      const blobUrl = doc.output('bloburl');
      window.open(blobUrl, '_blank');
      toast.dismiss('preview-toast');
    } catch (err) {
      console.error('Invoice view failed:', err);
      toast.dismiss('preview-toast');
      toast.error('Failed to open invoice');
    }
  };

  const renderInput = (label, name, value, type = "text", disabled = !isEditing, Icon, isLocked = false, required = false) => {
    const inputId = `profile-input-${name}`;
    const isRequired = required || (typeof label === 'string' && label.endsWith('*'));
    const displayLabel = typeof label === 'string' && label.endsWith('*') ? label.slice(0, -1).trim() : label;
    const isEditable = !disabled;

    return (
      <CustomInput
        id={inputId}
        name={name}
        type={type}
        label={
          <span className="inline-flex items-center gap-1">
            {displayLabel}
            {isLocked && <Lock size={12} className="text-slate-400" />}
          </span>
        }
        value={value || ''}
        onChange={handleInputChange}
        disabled={disabled}
        required={isEditable && isRequired}
        icon={Icon}
      />
    );
  };

  const value = {
    // Navigation & Tabs
    navigate,
    activeTab,
    setActiveTab,
    handleTabClick,
    validTabs,

    // Form state & operations
    formData,
    setFormData,
    isEditing,
    setIsEditing,
    isLoading,
    setIsLoading,
    handleInputChange,
    handleSave,
    handleLogout,
    renderInput,

    // Logo upload
    fileInputRef,
    handleLogoClick,
    handleLogoChange,
    handleRemoveLogo,

    // Password & OTP Reset
    showForgotPassword,
    setShowForgotPassword,
    otpMethod,
    setOtpMethod,
    otp,
    setOtp,
    otpSent,
    setOtpSent,
    otpVerified,
    setOtpVerified,
    handleSendOtp,
    handleOtpChange,
    handleOtpPaste,
    handleVerifyOtp,
    handleResetPasswordFinal,
    handleUpdatePasswordDirect,

    // GST & Invoice Email inline editing
    isEditingGst,
    setIsEditingGst,
    isEditingInvoice,
    setIsEditingInvoice,
    gstinValue,
    setGstinValue,
    gstNameValue,
    setGstNameValue,
    gstAddressValue,
    setGstAddressValue,
    invoiceEmailValue,
    setInvoiceEmailValue,
    handleSaveGst,
    handleSaveInvoiceEmail,

    // Subscription Data
    subscription,
    currentPlan,
    latestRequest,
    trialDaysRemaining,
    isTrialExpired,
    subscriptionDaysRemaining,
    subscriptionLoading,
    paymentHistory,
    vouchersCreated,
    vouchersLimit,
    cycleStartDate,
    supplierInfo,
    refreshSubscription,
    effectiveSub,
    isTrialUser,

    // Subscription Actions
    allPlans,
    showCycModal,
    setShowCycModal,
    showPmModal,
    setShowPmModal,
    showCancelModal,
    setShowCancelModal,
    showDowngradeModal,
    setShowDowngradeModal,
    selectedDowngradePlan,
    setSelectedDowngradePlan,
    showSwitchMonthlyModal,
    setShowSwitchMonthlyModal,
    showAnnualSupportModal,
    setShowAnnualSupportModal,
    showUpgradeModal,
    setShowUpgradeModal,
    selectedUpgradePlan,
    setSelectedUpgradePlan,
    isUpgrading,
    setIsUpgrading,
    isSwitchingAnnual,
    setIsSwitchingAnnual,
    isUpdatingPaymentMethod,
    setIsUpdatingPaymentMethod,
    isAuthorizingMandate,
    setIsAuthorizingMandate,
    showCheckoutPage,
    setShowCheckoutPage,
    requestReason,
    setRequestReason,
    requestComments,
    setRequestComments,
    isSubmittingRequest,
    requestSuccessMessage,
    setRequestSuccessMessage,
    handleUpgradeClick,
    handleConfirmUpgrade,
    triggerRazorpayMandate,
    handleCancelSubscriptionRequest,
    handleDowngradeRequest,
    handleSwitchMonthlyRequest,
    handleAuthorizePendingPlan,
    handleCancelSubscription,
    handleResumeSubscription,
    handleSwitchToAnnual,
    handleUpdatePaymentMethod,

    // Alerts
    dismissedRejectedId,
    handleDismissRejectedBanner,

    // Invoices
    handleDownloadInvoice,
    handleViewInvoice,
    formatDate,
    formatCurrency,
    getPaymentMethodLabel
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export default ProfileContext;
